import React from 'react';
import { 
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

interface FilePreviewProps {
  document: {
    id: string;
    title: string;
    fileName?: string;
    fileType?: string;
    downloadUrl?: string;
  };
  className?: string;
  fallbackColor: string;
  FileIcon: React.ComponentType<any>;
}

export default function FilePreview({ document, className = '', fallbackColor, FileIcon }: FilePreviewProps) {
  const isImage = document.fileType?.startsWith('image/');
  const isPdf = document.fileType === 'application/pdf';
  const isVideo = document.fileType?.startsWith('video/');
  const isAudio = document.fileType?.startsWith('audio/');

  // Image files - show actual preview
  if (isImage && document.downloadUrl) {
    return (
      <div className={`${className} relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800`}>
        <img 
          src={document.downloadUrl} 
          alt={document.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // Fallback to icon if image fails to load
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<div class="flex items-center justify-center w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"><svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
            }
          }}
        />
      </div>
    );
  }

  // PDF files - show PDF icon with preview hint
  if (isPdf) {
    return (
      <div className={`${className} relative flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden`}>
        <DocumentTextIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
        <div className="absolute bottom-0 left-0 right-0 bg-red-600/90 text-white text-[8px] font-bold text-center py-0.5">
          PDF
        </div>
      </div>
    );
  }

  // Video files - show icon only (no preview to avoid breaking storage lifecycle)
  if (isVideo) {
    return (
      <div className={`${className} flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg border border-gray-200 dark:border-gray-600`}>
        <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    );
  }

  // Audio files - show icon only (no preview to avoid breaking storage lifecycle)
  if (isAudio) {
    return (
      <div className={`${className} flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg border border-gray-200 dark:border-gray-600`}>
        <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.817L4.215 13.6a1.5 1.5 0 00-.954-.334H2a1 1 0 01-1-1V7.667a1 1 0 011-1h1.261a1.5 1.5 0 00.954-.334L8.383 3.076zM15 5a1 1 0 011 1v8a1 1 0 11-2 0V6a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  // Default file icon fallback
  return (
    <div 
      className={`flex items-center justify-center ${className}`}
      style={{ backgroundColor: fallbackColor + '10' }}
    >
      <FileIcon 
        className="w-12 h-12"
        style={{ color: fallbackColor }}
      />
    </div>
  );
}
